import { convertWithKeyAssist, type ConversionMode, type KeyAssistStrength } from "./key-assist.ts";
import { advanceVoicePitchFrame, EMPTY_VOICE_DISPLAY_STATE } from "./note-events.ts";
import { GRID_BEATS, type GridValue, type MidiNote, type PitchFrame } from "./music.ts";

export const DEFAULT_TAP_LATENCY_MS = 70;
export const RHYTHM_HOLD_THRESHOLD_MS = 180;
const BOUNDARY_GUARD_MS = 45;
const MIN_GUIDED_NOTE_BEATS = 1 / 32;

export type RhythmGuideGesture = {
  id: number;
  sequenceIndex: number;
  startTime: number;
  endTime: number;
  durationMs: number;
  startBeat: number;
};

export type RhythmGuideCandidate = {
  midi: number;
  score: number;
  share: number;
  frameCount: number;
};

export type RhythmGuideSegment = {
  gestureId: number;
  sequenceIndex: number;
  rawStartTime: number;
  rawEndTime: number;
  adjustedStartTime: number;
  adjustedEndTime: number;
  startBeat: number;
  durationBeats: number;
  holdDurationMs: number;
  held: boolean;
  dominantMidi: number | null;
  dominance: number;
  candidates: RhythmGuideCandidate[];
};

export type RhythmGuidedResult = {
  gestures: RhythmGuideGesture[];
  segments: RhythmGuideSegment[];
  notes: MidiNote[];
  droppedSegmentCount: number;
  latencyMs: number;
};

export type RhythmGuidedOptions = {
  bpm: number;
  grid: GridValue;
  latencyMs?: number;
  confidenceThreshold?: number;
  captureDurationSeconds?: number;
  snapToGrid?: boolean;
  minimumStartTimeSeconds?: number;
};

export type RhythmGuidedKeyAssistOptions = RhythmGuidedOptions & {
  conversionMode: ConversionMode;
  keyAssistStrength: KeyAssistStrength;
  keyAssistWeight?: number;
};

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function frameDurations(frames: PitchFrame[]): number[] {
  const positive = frames.slice(1).map((frame, index) => frame.time - frames[index].time).filter((value) => value > 0 && value <= 0.2);
  const fallback = Math.max(0.01, Math.min(0.1, median(positive) || 0.055));
  return frames.map((frame, index) => Math.max(0.01, Math.min(0.15, frames[index + 1]?.time - frame.time || fallback)));
}

function lightSnapBeat(beat: number, gridBeats: number): number {
  const nearest = Math.round(beat / gridBeats) * gridBeats;
  return Math.abs(nearest - beat) <= gridBeats * 0.3 ? nearest : beat;
}

function normalizeGestures(rawGestures: RhythmGuideGesture[], bpm: number): RhythmGuideGesture[] {
  const secondsPerBeat = 60 / bpm;
  return [...rawGestures]
    .sort((a, b) => a.startTime - b.startTime || a.sequenceIndex - b.sequenceIndex)
    .map((gesture, sequenceIndex) => ({
      ...gesture,
      sequenceIndex,
      endTime: Math.max(gesture.startTime, gesture.endTime),
      durationMs: Math.max(0, (gesture.endTime - gesture.startTime) * 1000),
      startBeat: gesture.startTime / secondsPerBeat,
    }));
}

function dominantPitch(
  frames: PitchFrame[],
  frameSeconds: number[],
  stableNotes: Array<number | null>,
  startTime: number,
  endTime: number,
  confidenceThreshold: number,
): { midi: number | null; dominance: number; candidates: RhythmGuideCandidate[] } {
  const scores = new Map<number, { score: number; frames: number }>();
  frames.forEach((frame, index) => {
    if (frame.time < startTime || frame.time >= endTime || frame.midiFloat === null) return;
    const threshold = frame.acceptanceConfidence ?? confidenceThreshold;
    if (frame.confidence < threshold) return;
    const stable = stableNotes[index];
    const midi = stable !== null && Math.abs(frame.midiFloat - stable) <= 0.45 ? stable : Math.round(frame.midiFloat);
    const edgeDistanceMs = Math.min(frame.time - startTime, endTime - frame.time) * 1000;
    const boundaryWeight = edgeDistanceMs < BOUNDARY_GUARD_MS ? 0.35 : 1;
    const centeringWeight = Math.max(0.35, 1 - Math.abs(frame.midiFloat - midi) * 0.65);
    const weight = Math.max(0.01, frame.confidence) * frameSeconds[index] * boundaryWeight * centeringWeight;
    const current = scores.get(midi) ?? { score: 0, frames: 0 };
    current.score += weight;
    current.frames += 1;
    scores.set(midi, current);
  });
  const total = Array.from(scores.values()).reduce((sum, candidate) => sum + candidate.score, 0);
  const candidates = Array.from(scores, ([midi, candidate]) => ({
    midi,
    score: candidate.score,
    share: total > 0 ? candidate.score / total : 0,
    frameCount: candidate.frames,
  })).sort((a, b) => b.score - a.score || b.frameCount - a.frameCount);
  return { midi: candidates[0]?.midi ?? null, dominance: candidates[0]?.share ?? 0, candidates };
}

export function buildRhythmGuidedNotes(
  frames: PitchFrame[],
  rawGestures: RhythmGuideGesture[],
  options: RhythmGuidedOptions,
): RhythmGuidedResult {
  const bpm = Math.max(1, options.bpm);
  const secondsPerBeat = 60 / bpm;
  const gridBeats = GRID_BEATS[options.grid];
  const latencyMs = Math.max(0, Math.min(250, options.latencyMs ?? DEFAULT_TAP_LATENCY_MS));
  const latencySeconds = latencyMs / 1000;
  const confidenceThreshold = options.confidenceThreshold ?? 0.55;
  const gestures = normalizeGestures(rawGestures, bpm);
  const orderedFrames = [...frames].sort((a, b) => a.time - b.time);
  const durations = frameDurations(orderedFrames);
  let voiceState = { ...EMPTY_VOICE_DISPLAY_STATE };
  const stableNotes = orderedFrames.map((frame) => {
    voiceState = advanceVoicePitchFrame(voiceState, frame, confidenceThreshold, 2, 3);
    return voiceState.stableNote;
  });
  const acceptedFrames = orderedFrames.filter((frame) => frame.midiFloat !== null && frame.confidence >= (frame.acceptanceConfidence ?? confidenceThreshold));
  const lastAcceptedEnd = acceptedFrames.length
    ? acceptedFrames.at(-1)!.time + (durations[orderedFrames.indexOf(acceptedFrames.at(-1)!)] ?? 0.055)
    : 0;
  const captureEnd = Math.max(options.captureDurationSeconds ?? 0, orderedFrames.at(-1)?.time ?? 0, gestures.at(-1)?.endTime ?? 0);
  let previousStartBeat = Number.NEGATIVE_INFINITY;

  const segments = gestures.map((gesture, index): RhythmGuideSegment => {
    const adjustedStartTime = Math.max(options.minimumStartTimeSeconds ?? 0, gesture.startTime - latencySeconds);
    const nextStartTime = gestures[index + 1] ? Math.max(adjustedStartTime + 0.001, gestures[index + 1].startTime - latencySeconds) : null;
    const held = gesture.durationMs >= RHYTHM_HOLD_THRESHOLD_MS;
    const holdEnd = adjustedStartTime + gesture.durationMs / 1000;
    const adjustedEndTime = nextStartTime ?? Math.max(adjustedStartTime + 0.01, held ? Math.max(holdEnd, lastAcceptedEnd) : (lastAcceptedEnd || captureEnd));
    const rawStartBeat = adjustedStartTime / secondsPerBeat;
    let startBeat = options.snapToGrid === false ? rawStartBeat : lightSnapBeat(rawStartBeat, gridBeats);
    if (startBeat <= previousStartBeat) startBeat = Math.max(rawStartBeat, previousStartBeat + MIN_GUIDED_NOTE_BEATS);
    previousStartBeat = startBeat;
    const pitch = dominantPitch(orderedFrames, durations, stableNotes, adjustedStartTime, adjustedEndTime, confidenceThreshold);
    return {
      gestureId: gesture.id,
      sequenceIndex: gesture.sequenceIndex,
      rawStartTime: gesture.startTime,
      rawEndTime: gesture.endTime,
      adjustedStartTime,
      adjustedEndTime,
      startBeat,
      durationBeats: 0,
      holdDurationMs: gesture.durationMs,
      held,
      dominantMidi: pitch.midi,
      dominance: pitch.dominance,
      candidates: pitch.candidates,
    };
  });

  segments.forEach((segment, index) => {
    const nextStart = segments[index + 1]?.startBeat;
    const rawEndBeat = segment.adjustedEndTime / secondsPerBeat;
    const snappedEnd = options.snapToGrid === false ? rawEndBeat : lightSnapBeat(rawEndBeat, gridBeats);
    const endBeat = nextStart ?? Math.max(segment.startBeat + MIN_GUIDED_NOTE_BEATS, snappedEnd);
    segment.durationBeats = Math.max(MIN_GUIDED_NOTE_BEATS, endBeat - segment.startBeat);
  });

  const notes = segments
    .filter((segment): segment is RhythmGuideSegment & { dominantMidi: number } => segment.dominantMidi !== null)
    .map((segment) => ({
      midi: segment.dominantMidi,
      startBeat: segment.startBeat,
      durationBeats: segment.durationBeats,
      velocity: Math.round(82 + Math.min(1, segment.dominance) * 28),
    }));
  return {
    gestures,
    segments,
    notes,
    droppedSegmentCount: segments.length - notes.length,
    latencyMs,
  };
}

export function convertRhythmGuidedWithKeyAssist(
  frames: PitchFrame[],
  gestures: RhythmGuideGesture[],
  options: RhythmGuidedKeyAssistOptions,
) {
  const guided = buildRhythmGuidedNotes(frames, gestures, options);
  const assisted = convertWithKeyAssist(
    options.conversionMode,
    frames,
    guided.notes,
    options.bpm,
    options.keyAssistStrength,
    new Set(),
    options.keyAssistWeight,
  );
  return { ...guided, notes: assisted.notes, keySegments: assisted.segments, changedNoteKeys: assisted.changedNoteKeys };
}
